import { Button, Tag } from "antd";
import i18n from "@/utils/i18n";
import {
  VideoCameraOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  EducationType,
  type VirtualClassroomDto,
} from "../../../types/client-education-detail.types";
import styles from "./VirtualClassrooms.module.scss";

interface ClassroomState {
  label: string;
  canJoin: boolean;
  live: boolean;
  past: boolean;
  /** Gösterilecek tarih/saat aralığı — scheduledTimes yoksa vc.startDate/endDate'ten türetilir. */
  rangeText: string | null;
  dateText: string | null;
}

function classifyNow(vc: VirtualClassroomDto, now: Date): ClassroomState {
  const nowMs = now.getTime();
  const times = vc.scheduledTimes ?? [];

  // 1) scheduledTimes varsa öncelik onlardadır.
  if (times.length) {
    const sorted = [...times].sort((a, b) => {
      const ka = dayjs(`${a.date}T${a.startTime ?? "00:00"}`).valueOf();
      const kb = dayjs(`${b.date}T${b.startTime ?? "00:00"}`).valueOf();
      return ka - kb;
    });
    for (const t of sorted) {
      const start = dayjs(`${t.date}T${t.startTime ?? "00:00"}`);
      const end = dayjs(`${t.date}T${t.endTime ?? "23:59"}`);
      if (!start.isValid() || !end.isValid()) continue;
      const s = start.valueOf();
      const e = end.valueOf();
      const dateText = start.format("DD MMM YYYY");
      const rangeText =
        t.startTime && t.endTime ? `${t.startTime}–${t.endTime}` : null;
      if (nowMs > e) continue;
      const windowStart = s - 15 * 60_000;
      if (nowMs >= windowStart && nowMs < s) {
        return {
          label: `Başlıyor · ${start.format("HH:mm")}`,
          canJoin: true,
          live: false,
          past: false,
          dateText,
          rangeText,
        };
      }
      if (nowMs >= s && nowMs <= e) {
        return {
          label: i18n.t("virtualClassroomCard.live"),
          canJoin: true,
          live: true,
          past: false,
          dateText,
          rangeText,
        };
      }
      return {
        label: `Planlandı · ${start.format("DD MMM HH:mm")}`,
        canJoin: false,
        live: false,
        past: false,
        dateText,
        rangeText,
      };
    }
    return {
      label: "Bitti",
      canJoin: false,
      live: false,
      past: true,
      dateText: null,
      rangeText: null,
    };
  }

  // 2) scheduledTimes yoksa classroom kendi startDate/endDate alanlarına bak.
  if (vc.startDate || vc.endDate) {
    const start = vc.startDate ? dayjs(vc.startDate) : null;
    const end = vc.endDate ? dayjs(vc.endDate) : null;
    const s = start?.valueOf() ?? null;
    const e = end?.valueOf() ?? null;
    const dateText = start?.format("DD MMM YYYY") ?? end?.format("DD MMM YYYY") ?? null;
    const rangeText =
      start && end
        ? `${start.format("HH:mm")}–${end.format("HH:mm")}`
        : null;
    if (e != null && nowMs > e) {
      return {
        label: "Bitti",
        canJoin: false,
        live: false,
        past: true,
        dateText,
        rangeText,
      };
    }
    if (s != null && e != null && nowMs >= s && nowMs <= e) {
      return {
        label: "Devam Ediyor",
        canJoin: true,
        live: true,
        past: false,
        dateText,
        rangeText,
      };
    }
    if (s != null && nowMs < s) {
      return {
        label: `Planlandı · ${start!.format("DD MMM HH:mm")}`,
        canJoin: false,
        live: false,
        past: false,
        dateText,
        rangeText,
      };
    }
    return {
      label: i18n.t("virtualClassroomCard.unscheduled"),
      canJoin: false,
      live: false,
      past: false,
      dateText,
      rangeText,
    };
  }

  return {
    label: i18n.t("virtualClassroomCard.unscheduled"),
    canJoin: false,
    live: false,
    past: false,
    dateText: null,
    rangeText: null,
  };
}

export function VirtualClassroomCard(props: {
  vc: VirtualClassroomDto;
  now: Date;
  educationType?: EducationType;
  onJoin: (vc: VirtualClassroomDto) => void;
}) {
  const { vc, now, educationType, onJoin } = props;
  const s = classifyNow(vc, now);
  const isOnSite = educationType === EducationType.ON_SITE;
  const showJoinButton = !isOnSite;

  return (
    <article className={styles.card}>
      <header>
        <h3>{vc.name ?? i18n.t("virtualClassroomCard.fallbackName")}</h3>
        <Tag
          color={
            s.live ? "red" : s.canJoin ? "blue" : s.past ? "default" : "default"
          }
        >
          {s.label}
        </Tag>
      </header>
      {(s.dateText || s.rangeText) && (
        <div className={styles.meta}>
          {s.dateText && <span>📅 {s.dateText}</span>}
          {s.rangeText && <span>⏱ {s.rangeText}</span>}
        </div>
      )}
      {vc.location && (
        <div className={styles.meta}>
          <a
            className={styles.locationLink}
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vc.location)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <EnvironmentOutlined /> {vc.location}
          </a>
        </div>
      )}
      {vc.myParticipationMinutes != null && s.past && (
        <div className={styles.pastNote}>
          Katıldın · {vc.myParticipationMinutes} dk
        </div>
      )}
      {/*
       * Buton sadece gerçekten aksiyon alınabilecek durumlarda renderlanır.
       * Bitti / Planlandı gibi durumlarda üstteki Tag yeterli; disabled gri
       * buton hem görsel olarak bozuk hem de yanıltıcı.
       */}
      {showJoinButton && s.canJoin && (
        <Button
          type="primary"
          icon={<VideoCameraOutlined />}
          className={styles.joinPulse}
          onClick={() => onJoin(vc)}
        >
          {s.live ? i18n.t("virtualClassroomCard.joinLive") : i18n.t("virtualClassroomCard.joinPending")}
        </Button>
      )}
    </article>
  );
}
